<template>
  <aside
    class="border-l border-grey-200 bg-white w-1/4 min-w-sm -mb-16 z-50"
  >
    <form
      class="h-screen overflow-auto sticky pin-t flex flex-col"
      @submit.prevent="saveAssessment"
    >
      <header class="px-6 py-4 shadow sticky pin-t z-10 bg-white">
        <div class="h-12 flex items-center">
          <h1 class="text-xl leading-none">
            Assessment
          </h1>
          <p class="ml-auto">
            <span
              class="text-5xl font-bold leading-none"
              :class="gradingColorScore"
            >
              {{ totalScore }}/<small class="text-2xl">{{ maxScore }}</small>
            </span>
          </p>
        </div>
      </header>
      <fieldset
        v-for="(area, index) in assessmentAreas"
        :key="index"
      >
        <legend
          class="border-b border-grey-200 px-6 py-4 font-bold w-full"
          :class="{
            'border-t': index > 0
          }"
        >
          {{ area.title }}
        </legend>
        <div
          v-for="(question, qi) in area.questions"
          :key="question.key"
          class="mx-6 my-4"
          :class="{
            'border-b-2 pb-4' : qi + 1 !== area.questions.length,
            'text-negative-800' : errors[question.key] && showErrors
          }"
        >
          <div class="flex items-start">
            <div
              class="flex-1 mb-4"
              v-html="parseMarkdown(question.title)"
            />
            <div
              v-if="question.notes"
              class="ml-4 flex-no-shrink"
            >
              <popper
                trigger="click"
                :options="{
                  placement: 'bottom'
                }"
                @show="toggleFocusInput(`note_${question.key}`, true)"
                @hide="toggleFocusInput(`note_${question.key}`)"
              >
                <div
                  class="bg-white shadow-lg p-4 z-100 relative w-screen max-w-sm border-t-4 border-grey-500"
                >
                  <label
                    :for="`${question.key}_note`"
                    class="text-xs font-bold block text-grey-700 mb-1"
                  >
                    Notes for question
                  </label>
                  <textarea
                    :id="`${question.key}_note`"
                    :ref="`note_${question.key}`"
                    v-model="notes[question.key]"
                    :name="`${question.key}_note`"
                    :disabled="gradingLocked"
                    :placeholder="
                      `Please enter your notes for this question's grade`
                    "
                    class="focus:outline-none w-full leading-normal text-grey-600 focus:text-black transition-all transition-normal h-32 resize-none p-0"
                  />
                </div>
                <button
                  slot="reference"
                  type="button"
                  class="flex items-center focus:outline-none"
                >
                  <icon
                    icon="chat"
                    class="w-6 h-6"
                    :class="{
                      'icons-primary': notes[question.key],
                      'icons-grey': !notes[question.key]
                    }"
                  />
                </button>
              </popper>
            </div>
          </div>
          <template v-if="question.type === 'boolean'">
            <div>
              <label
                v-for="(option, oi) in question.options"
                :key="oi"
                class="mr-4 inline-flex items-center my-2"
                :class="{
                  'cursor-pointer' : !gradingLocked
                }"
              >
                <input
                  v-model="grades[question.key]"
                  type="radio"
                  :value="option.value"
                  class="sr-only"
                  :disabled="gradingLocked"
                >
                <div
                  class="w-4 h-4 border-2 p-2px mr-1 flex items-stretch"
                  :class="{
                    'hover:border-positive-500' : !gradingLocked,
                    'border-positive-500': grades[question.key] === option.value
                  }"
                  aria-hidden="hidden"
                >
                  <div
                    class="flex-1"
                    :class="{
                      'bg-positive-500' : grades[question.key] === option.value
                    }"
                  ></div>
                </div>
                <span v-html="parseMarkdown(option.label)" />
              </label>
            </div>
          </template>
          <template v-else-if="question.type === 'select'">
            <div class="w-full border-2 p-2 mt-2 flex focus-within:border-primary-500">
              <select
                v-model="grades[question.key]"
                class="appearance-none focus:outline-none flex-1 items-center -mr-6 w-full pr-6"
                :disabled="gradingLocked"
              >
                <option
                  value
                  disabled
                >
                  Please select...
                </option>
                <option
                  v-for="(option, oi) in question.options"
                  :key="oi"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
              <svg
                class="h-5 w-5 text-grey-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </template>
          <template v-else-if="question.type === 'option'">
            <div>
              <label
                v-for="(option, oi) in question.options"
                :key="oi"
                class="group items-baseline my-2 border hover:border-positive-300 transition-all transition-normal"
                :class="{
                  'border-positive-300': grades[question.key] === option.value,
                  'cursor-pointer' : !gradingLocked,
                  'inline-flex mr-2': typeof option === 'number',
                  'flex p-2' : typeof option !== 'number',
                }"
              >
                <input
                  v-model="grades[question.key]"
                  type="radio"
                  :value="option.value || option"
                  class="sr-only"
                  :disabled="gradingLocked"
                >
                <div
                  class="flex items-stretch flex-no-shrink"
                  :class="{
                    'group-hover:border-positive-500' : !gradingLocked,
                    'border-positive-500': grades[question.key] === (option.value || option),
                    'w-10 h-10': typeof option === 'number',
                    'w-6 h-6 mr-2 border-2 p-2px': typeof option !== 'number',
                  }"
                  aria-hidden="hidden"
                >
                  <div
                    class="flex-1 text-xs flex items-center justify-center font-bold leading-none"
                    :class="{
                      'bg-positive-500 text-positive-100' : grades[question.key] === (option.value || option)
                    }"
                  ><template v-if="question.scorable">{{ option.value || option }}</template></div>
                </div>
                <span v-if="option.label" v-html="parseMarkdown(option.label)" />
              </label>
            </div>
          </template>
          <template v-else-if="question.type === 'textarea'">
            <textarea
              ref="textarea"
              v-model="grades[question.key]"
              :required="question.required"
              rows="3"
              class="w-full border-2 mt-2 rounded-none focus:outline-none focus:shadow-outline p-2 resize-none"
              :disabled="gradingLocked"
            ></textarea>
          </template>
          <div
            v-if="errors[question.key] && showErrors"
            class="text-negative-800 text-sm"
          >
            {{ errors[question.key].join(", ") }}
          </div>
        </div>
      </fieldset>
      <div class="sticky pin-b z-10 mt-auto">
        <template v-if="!gradingLocked">
          <div
            v-if="grading_completed"
            class="bg-positive-100 px-6 py-4 text-positive-800 border-t border-positive-300 font-semibold"
          >
            Assessment complete
          </div>
          <div
            v-else
            class="bg-neutral-100 px-6 py-4 text-neutral-800 border-t border-neutral-300 font-semibold flex"
          >
            <p>Incomplete assessment</p>
            <button
              type="button"
              class="ml-auto text-neutral-800 uppercase focus:outline-none text-xs font-bold hover:border-neutral-600"
              @click="showErrors = !showErrors"
            >
              {{ showErrors ? 'Hide' : 'Show' }} Incomplete ({{ errorCount }})
            </button>
          </div>
        </template>
        <div class="border-t mt-4 px-6 py-4 bg-white mt-auto flex items-center">
          <div class="w-32">
            <app-button
              tag="a"
              :href="route(`assessments.phases.view`, [$page.funding_pot.id, $page.marking_phase.id])"
            >
              {{ gradingLocked ? 'Exit' : 'Save &amp; Exit' }}
            </app-button>
          </div>
        </div>
      </div>
    </form>
  </aside>
</template>

<script>
import Popper from "vue-popperjs";
import debounce from 'lodash/debounce'
import autosize from "autosize";

export default {
  name: 'AssessmentSidebar',
  components: { Popper },
  props: {
    assessmentTemplate: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showErrors: false,
      scorableQuestions: [],
      notes: {},
      grades: {},
      errors: {},
      grading_completed: false
    }
  },
  computed: {
    gradingLocked() {
      return this.$page.marking_phase.locked || this.$page.project.marking_phase_status.confirmed_at
    },
    errorCount() {
      return Object.keys(this.errors).length
    },
    assessmentAreas() {
      return this.assessmentTemplate?.assessment_areas ?? []
    },
    grading() {
      return this.$page.grading
    },
    totalScore() {
      let total = 0;
      for (let key of this.scorableQuestions) {
        total+= this.grades?.[key] ?? 0
      }
      return total;
    },
    maxScore() {
      return this.assessmentTemplate.max_score
    },
    gradingColorScore() {
      if(!this.totalScore || !this.maxScore) {
        return 'text-grey-500'
      }
      return {
        0: 'text-grading-value-1',
        10: 'text-grading-value-2',
        20: 'text-grading-value-3',
        30: 'text-grading-value-4',
        40: 'text-grading-value-5',
        50: 'text-grading-value-6',
        60: 'text-grading-value-7',
        70: 'text-grading-value-8',
        80: 'text-grading-value-9',
        90: 'text-grading-value-10',
        100: 'text-grading-value-10',
      }[Math.floor((this.totalScore / this.maxScore)*10)*10]
    }
  },
  watch: {
    grades: {
      handler() {
        this.saveAssessment()
      },
      deep: true
    },
    notes: {
      handler() {
        this.saveAssessment()
      },
      deep: true
    }
  },
  mounted() {
    autosize(this.$refs.textarea)
  },
  created() {

    this.grading_completed = this.grading.grading_completed ?? false;
    // loop all assessment areas in assessment template
    for(let area of this.assessmentAreas) {
      // loop all questions in assessment template
      for(let question of area.questions) {

        // check if question is scorable from assessment structure
        if(question.scorable) {
          this.scorableQuestions.push(question.key)
        }

        // Check for special keys as these appear on the assessor grading model
        if(['comments','comments_additional','recommended'].includes(question.key)) {
          // set the grade for either comments or recommended if they exist, or null
          this.$set(this.grades, question.key, this.grading?.[question.key] ?? null)

        } else if (question.type === 'textarea') {
          let q = this.findEntryByKey(question.key);

          this.$set(this.grades, question.key, q.value);

          if(question.notes) {
            this.$set(this.notes, question.key, q.note ?? null)
          }

        } else {

          // all other keys will be scores so find on grades relationship, or default to null
          let score = this.findEntryByKey(question.key)

          this.$set(this.grades, question.key, score.grade ?? null)

          if(question.notes) {
            this.$set(this.notes, question.key, score.note ?? null)
          }

        }
      }
    }
  },
  methods: {
    toggleFocusInput(ref, focus = false) {
      if (focus) {
        setTimeout(() => this.$refs[ref][0].focus(), 50);
      }
      this.$refs[ref][0].blur();
    },
    saveAssessment: debounce(function() {
      if(this.gradingLocked) return
      axios({
        method: 'post',
        url: window.location.href,
        data: {
          notes: this.notes,
          grades: this.grades
        }
      }).then(({ data }) => {
        this.grading_completed = data.completed
        this.errors = data.errors
      }).catch(() => {
        alert('Unable to save assessment, please try again.')
        throw new Error('Unable to save assessment.')
      })
    }, 300),
    findEntryByKey(key) {
      return this.grading?.scores?.find((entry) => entry.key === key) ?? false
    },
  },
}
</script>
